import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import { tscColor } from '../../utils/tsc';
import './PolicyTemplatesModal.css';


function PolicyTemplatesModal({ onClose, onUse }) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [using, setUsing]         = useState(null);

  useEffect(() => {
    axiosInstance.get('/policies/templates')
      .then(res => setTemplates(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = templates.filter(tpl =>
    !search ||
    (tpl.policyName || '').toLowerCase().includes(search.toLowerCase()) ||
    (tpl.tscTags || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleUse = async (template) => {
    setUsing(template.policyId);
    try {
      await axiosInstance.post(`/organization-policies/from-template/${template.policyId}`);
      onUse && onUse(template);
      onClose();
    } catch {
      alert(t('PolicyTemplatesModal.createError'));
    } finally {
      setUsing(null);
    }
  };

  return (
    <div className="ptm-overlay" onClick={onClose}>
      <div className="ptm-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="ptm-header">
          <div className="ptm-header-left">
            <i className="fas fa-layer-group ptm-header-icon" />
            <div>
              <h2 className="ptm-title">{t('PolicyTemplatesModal.title')}</h2>
              <p className="ptm-subtitle">{t('PolicyTemplatesModal.subtitle')}</p>
            </div>
          </div>
          <button className="ptm-close" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Search */}
        <div className="ptm-search-wrap">
          <i className="fas fa-search ptm-search-icon" />
          <input
            className="ptm-search"
            placeholder={t('PolicyTemplatesModal.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        {/* Content */}
        <div className="ptm-body">
          {loading ? (
            <div className="ptm-spinner-wrap"><div className="ptm-spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="ptm-empty">
              <i className="fas fa-search" />
              <p>{t('PolicyTemplatesModal.emptyState')}</p>
            </div>
          ) : (
            <div className="ptm-grid">
              {filtered.map(tpl => (
                <div key={tpl.policyId} className="ptm-card">
                  <div className="ptm-card-top">
                    <div className="ptm-card-icon-wrap">
                      <i className="fas fa-file-shield ptm-card-icon" />
                    </div>
                    <div className="ptm-card-info">
                      <div className="ptm-card-name">{tpl.policyName}</div>
                      {tpl.description && (
                        <p className="ptm-card-desc">{tpl.description}</p>
                      )}
                    </div>
                  </div>
                  {tpl.tscTags && (
                    <div className="ptm-tags">
                      {tpl.tscTags.split(',').map(tag => tag.trim()).filter(Boolean).map(tag => (
                        <span
                          key={tag}
                          className="ptm-tag"
                          style={{ background: tscColor(tag) }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <button
                    className="ptm-use-btn"
                    disabled={using === tpl.policyId}
                    onClick={() => handleUse(tpl)}
                  >
                    {using === tpl.policyId
                      ? <><i className="fas fa-spinner fa-spin" /> {t('PolicyTemplatesModal.adding')}</>
                      : <><i className="fas fa-plus" /> {t('PolicyTemplatesModal.useTemplate')}</>}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ptm-footer">
          <span className="ptm-count">{t('PolicyTemplatesModal.count', { count: filtered.length })}</span>
          <button className="ptm-cancel" onClick={onClose}>{t('PolicyTemplatesModal.close')}</button>
        </div>
      </div>
    </div>
  );
}

export default PolicyTemplatesModal;
